/**
 * Fleet API surface — typed wrappers over the /fleet/* endpoints in
 * nurev-api. Every function here maps 1:1 to a backend controller in
 * `nurev-api/app/controllers/fleet.js`. No new endpoints invented here;
 * the entire backend is already shipped (see FLEET_CONSOLE_SCOPE.md).
 */
import { client, unwrap } from './client';

// ─── shared types ─────────────────────────────────────────────────────

export type NodeStatus = 'active' | 'paused' | 'offline' | 'decommissioned';
export type Platform = 'linux' | 'windows' | 'mac';
export type Stream =
  | 'cpu_compute'
  | 'gpu_compute'
  | 'storage'
  | 'bandwidth'
  | 'mixed';

// ─── overview / stats ─────────────────────────────────────────────────

export interface FleetStats {
  totalNodes: number;
  activeNodes: number;
  pausedNodes: number;
  offlineNodes: number;
  totalHashrate: number;
  totalLiveTime: number;
  totalEarnings: number;
  totalSharesAccepted: number;
  totalSharesRejected: number;
  avgCpuUsage: number;
  avgMemUsageMb: number;
  totalCpuCores: number;
  totalRamMb: number;
  platformBreakdown: Array<{ _id: Platform; count: number; hashrate: number; earnings: number }>;
  groupBreakdown: Array<{ _id: string; count: number; activeCount: number }>;
}

export const getStats = (group?: string): Promise<FleetStats> =>
  unwrap(client.get('/fleet/stats', { params: group ? { group } : {} }));

// ─── earnings by stream ───────────────────────────────────────────────

export interface StreamSubtotal {
  stream: Stream;
  revAmount: number;
  usdAmount: number;
  blockCount: number;
  distinctNodes: number;
}

export interface EarningsByStream {
  period: string;
  sinceDateKey: string;
  totals: {
    revAmount: number;
    usdAmount: number;
    streamsWithActivity: number;
  };
  byStream: StreamSubtotal[];
  byDayStream: Array<{ dateKey: string; stream: Stream; revAmount: number; usdAmount: number }>;
}

export const getEarningsByStream = (days = 30): Promise<EarningsByStream> =>
  unwrap(client.get('/fleet/earnings-by-stream', { params: { days } }));

// ─── nodes ────────────────────────────────────────────────────────────

export interface FleetNode {
  _id: string;
  nodeKey: string;
  label?: string;
  hostname?: string;
  group: string;
  platform: Platform;
  status: NodeStatus;
  specs: { cpuCores: number; ramMb: number; gpu?: string };
  stats: {
    hashrate: number;
    liveTime: number;
    earnings: number;
    sharesAccepted: number;
    sharesRejected: number;
    cpuUsage?: number;
    memUsageMb?: number;
    uptime?: number;
  };
  lastHeartbeatAt?: string;
  createdAt: string;
}

export interface NodesPage {
  docs: FleetNode[];
  totalDocs: number;
  page: number;
  totalPages: number;
}

export interface ListNodesParams {
  page?: number;
  limit?: number;
  status?: NodeStatus | '';
  platform?: Platform | '';
  group?: string;
  search?: string;
}

export const listNodes = (params: ListNodesParams = {}): Promise<NodesPage> =>
  unwrap(client.get('/fleet/nodes', { params }));

export const getNode = (nodeKey: string): Promise<FleetNode> =>
  unwrap(client.get(`/fleet/nodes/${encodeURIComponent(nodeKey)}`));

export const updateNode = (
  nodeKey: string,
  updates: Partial<FleetNode & { label: string; status: NodeStatus }>
): Promise<FleetNode> =>
  unwrap(client.patch(`/fleet/nodes/${encodeURIComponent(nodeKey)}`, updates));

export const decommissionNode = (nodeKey: string): Promise<void> =>
  unwrap(client.delete(`/fleet/nodes/${encodeURIComponent(nodeKey)}`));

export interface RegisterNodesBody {
  count: number;
  group?: string;
  platform?: Platform;
  labelPrefix?: string;
}

export const registerNodes = (
  body: RegisterNodesBody
): Promise<{ registered: number; nodeKeys: string[] }> =>
  unwrap(client.post('/fleet/nodes', body));

export interface BulkUpdateBody {
  filter: { nodeKeys?: string[]; group?: string; status?: NodeStatus };
  update: Partial<{ status: NodeStatus; group: string }>;
}

export const bulkUpdate = (
  body: BulkUpdateBody
): Promise<{ matched: number; modified: number }> =>
  unwrap(client.post('/fleet/bulk-update', body));

// ─── groups ───────────────────────────────────────────────────────────

export type EnvironmentCert = 'bare_metal' | 'colo' | 'residential' | 'mobile';

export interface FleetGroup {
  _id: string;
  groupName: string;
  environmentCert: EnvironmentCert;
  region?: string;
  allowedStreams: Stream[];
  attestations: Array<{ stream: Stream; attestedAt: string; attestedBy: string }>;
  nodeCount?: number;
  createdAt: string;
}

export const listGroups = (): Promise<{ groups: FleetGroup[] }> =>
  unwrap(client.get('/fleet/groups'));

export const createGroup = (
  body: Omit<FleetGroup, '_id' | 'attestations' | 'nodeCount' | 'createdAt'>
): Promise<FleetGroup> =>
  unwrap(client.post('/fleet/groups', body));

export const updateGroup = (
  id: string,
  body: Partial<Omit<FleetGroup, '_id' | 'attestations' | 'createdAt'>>
): Promise<FleetGroup> =>
  unwrap(client.patch(`/fleet/groups/${id}`, body));

export const attestStream = (
  groupId: string,
  stream: Stream
): Promise<FleetGroup> =>
  unwrap(client.post(`/fleet/groups/${groupId}/attestation`, { stream }));

// ─── provisioning tokens ──────────────────────────────────────────────

export interface ProvisioningToken {
  _id: string;
  label: string;
  tokenSuffix: string;
  defaultGroup?: string;
  defaultPlatform?: Platform;
  defaultResourceLimits?: {
    cpuCeiling: number;
    memoryCapMb: number;
    priority: 'idle' | 'below_normal' | 'normal';
  };
  createdAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
}

export interface CreateTokenResponse {
  token: ProvisioningToken;
  provisioningToken: string; // raw value, shown ONCE
}

export const listProvisioningTokens = (): Promise<ProvisioningToken[]> =>
  unwrap(client.get('/fleet/provisioning-tokens'));

export const createProvisioningToken = (body: {
  label: string;
  defaultGroup?: string;
  defaultPlatform?: Platform;
  defaultResourceLimits?: ProvisioningToken['defaultResourceLimits'];
}): Promise<CreateTokenResponse> =>
  unwrap(client.post('/fleet/provisioning-tokens', body));

export const revokeProvisioningToken = (id: string): Promise<void> =>
  unwrap(client.delete(`/fleet/provisioning-tokens/${id}`));

// ─── statements ───────────────────────────────────────────────────────

export interface FleetStatement {
  _id: string;
  monthKey: string; // YYYY-MM
  status: 'pending' | 'ready' | 'regenerating' | 'failed';
  totalRev: number;
  totalUsd: number;
  generatedAt?: string;
  pdfUrl?: string;
  csvUrl?: string;
}

export const listStatements = (): Promise<FleetStatement[]> =>
  unwrap(client.get('/fleet/statements'));

export const getStatement = (id: string): Promise<FleetStatement> =>
  unwrap(client.get(`/fleet/statements/${id}`));

export const regenerateStatement = (id: string): Promise<FleetStatement> =>
  unwrap(client.post(`/fleet/statements/${id}/regenerate`));

// ─── alerts ───────────────────────────────────────────────────────────

export interface AlertRule {
  _id: string;
  name: string;
  trigger: 'nodes_offline_pct' | 'earnings_drop_pct' | 'audit_failure_rate';
  threshold: number;
  windowMinutes: number;
  enabled: boolean;
  notifyEmail?: string;
  createdAt: string;
}

export interface AlertEvent {
  _id: string;
  ruleId: string;
  ruleName: string;
  firedAt: string;
  context: Record<string, unknown>;
  acknowledgedAt?: string | null;
  acknowledgedBy?: string | null;
}

export const listAlertRules = (): Promise<AlertRule[]> =>
  unwrap(client.get('/fleet/alerts/rules'));

export const createAlertRule = (
  body: Omit<AlertRule, '_id' | 'createdAt'>
): Promise<AlertRule> => unwrap(client.post('/fleet/alerts/rules', body));

export const updateAlertRule = (
  id: string,
  body: Partial<Omit<AlertRule, '_id' | 'createdAt'>>
): Promise<AlertRule> => unwrap(client.patch(`/fleet/alerts/rules/${id}`, body));

export const deleteAlertRule = (id: string): Promise<void> =>
  unwrap(client.delete(`/fleet/alerts/rules/${id}`));

export const listAlertEvents = (params: { limit?: number } = {}): Promise<
  AlertEvent[]
> => unwrap(client.get('/fleet/alerts/events', { params }));

// ─── inference workers ────────────────────────────────────────────────

/**
 * Whitelisted reason codes the desktop InferenceWorker can report for
 * setupStatus.reasons[]. Anything outside this set is dropped server-side
 * — keep this in sync with KNOWN_SETUP_REASONS in
 * nurev-api/app/services/inferenceWorkerRegistry.js so the UI can format
 * any reason the server might surface.
 */
export type InferenceWorkerSetupReason =
  | 'ollama_not_running'
  | 'ollama_version_too_old'
  | 'no_models_pulled'
  | 'gpu_not_detected'
  | 'gpu_driver_mismatch'
  | 'insufficient_vram'
  | 'port_in_use'
  | 'config_missing';

export interface InferenceWorkerSetupStatus {
  ready: boolean;
  reasons: InferenceWorkerSetupReason[];
  details: Record<string, string | number | boolean | (string | number)[]>;
}

export interface InferenceWorker {
  workerId: string;
  machineId?: string | null;
  platform: string;
  gpu: { vendor: string | null; model: string | null; vramMb: number | null };
  ollamaVersion: string | null;
  supportedModels: string[];
  activeJobs: number;
  registeredAt: string | null;
  uptimeSec?: number;
  lastHeartbeatAt: string | null;
  lastHeartbeatSec?: number;
  setupStatus: InferenceWorkerSetupStatus;
  setupStatusUpdatedAt: string | null;
}

export interface InferenceWorkersResponse {
  workers: InferenceWorker[];
  summary: {
    total: number;
    ready: number;
    notReady: number;
    reasonCounts: Partial<Record<InferenceWorkerSetupReason, number>>;
  };
}

export const listInferenceWorkers = (): Promise<InferenceWorkersResponse> =>
  unwrap(client.get('/fleet/inference-workers'));

// ─── pending inference earnings ───────────────────────────────────────

export interface PendingInferenceEarnings {
  pending: {
    jobs: number;
    totalPriceMicrocents: number;
    operatorMicrocents: number;
    operatorCents: number;
    operatorUsd: number;
    oldestJobAt: string | null;
    newestJobAt: string | null;
  };
  lastSettlement: {
    at: string;
    cents: number;
    jobs: number;
  } | null;
  sharePercent: number;
}

export const getPendingInferenceEarnings = (): Promise<PendingInferenceEarnings> =>
  unwrap(client.get('/fleet/inference-earnings/pending'));
